import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
import { sendDemoEmail, DemoFormData } from '../lib/sendDemoEmail';
import { X as XIcon, CheckCircle2, Loader2 } from 'lucide-react';
export function Nav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Marketplace',
    href: '#marketplace'
  }, {
    name: 'Features',
    href: '#features'
  }, {
    name: 'Investors',
    href: '#investors'
  }];
  return <>
      <motion.header initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }} className={cn('fixed top-0 left-0 right-0 z-50 transition-all duration-300', isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-[#E8E4DC] py-3' : 'bg-transparent py-5')} data-id="element-525">
        <div className="max-w-6xl mx-auto px-5 lg:px-8" data-id="element-526">
          <nav className="flex items-center justify-between" data-id="element-527">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2" data-id="element-528">
              <img src="https://cdn.magicpatterns.com/uploads/gPkoxER8MRU5GSVk7bdedN/LogoBlack.fdf2fa00.svg" alt="The Yarn Bazaar" className="h-8 w-auto" data-id="element-529" />
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8" data-id="element-530">
              {navLinks.map(link => <motion.a key={link.name} href={link.href} className="text-sm font-medium text-[#5C5A55] hover:text-[#0F0E0C] transition-colors nav-link-thread" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-531">
                  {link.name}
                </motion.a>)}
            </div>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-4" data-id="element-532">
              <motion.button onClick={() => setDemoOpen(true)} whileHover={{
              scale: 1.04
            }} whileTap={{
              scale: 0.97
            }} className="magnetic-btn px-5 py-2.5 bg-[#E8500A] hover:bg-[#D14508] text-white font-inter font-semibold rounded-full text-sm transition-colors" data-id="element-533">
                Request Demo
              </motion.button>
            </div>

            {/* Mobile Toggle */}
            <button className="md:hidden p-2 text-[#5C5A55]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-534">
              {mobileMenuOpen ? <X data-id="element-535" /> : <Menu data-id="element-536" />}
            </button>
          </nav>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-537">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-white border-b border-[#E8E4DC] overflow-hidden" data-id="element-538">
              <div className="max-w-6xl mx-auto px-5 py-6 flex flex-col gap-4" data-id="element-539">
                {navLinks.map(link => <a key={link.name} href={link.href} className="text-base font-medium text-[#0F0E0C] py-2 border-b border-[#E8E4DC]" onClick={() => setMobileMenuOpen(false)} data-id="element-540">
                    {link.name}
                  </a>)}
                <button onClick={() => {
              setMobileMenuOpen(false);
              setDemoOpen(true);
            }} className="w-full py-3 bg-[#E8500A] text-white font-inter font-semibold rounded-full text-sm mt-2" data-id="element-541">
                  Request Demo
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.header>

      {/* Demo Modal */}
      <AnimatePresence data-id="element-542">
        {demoOpen && <NavDemoModal onClose={() => setDemoOpen(false)} data-id="element-543" />}
      </AnimatePresence>
    </>;
}
// Inline modal for Nav
const roles = ['Mill Owner / Manufacturer', 'Trader / Broker', 'Exporter / Importer', 'Retailer / Brand', 'Investor', 'Other'];
function NavDemoModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<DemoFormData>({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await sendDemoEmail(form);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-544">
      <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-545" />
      <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" initial={{
      opacity: 0,
      scale: 0.94,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.94,
      y: 20
    }} transition={{
      ease: [0.34, 1.56, 0.64, 1],
      duration: 0.4
    }} data-id="element-546">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F4F0] flex items-center justify-center hover:bg-[#E8E4DC] transition-colors z-10" data-id="element-547">
          <XIcon className="w-4 h-4 text-[#5C5A55]" data-id="element-548" />
        </button>

        {!submitted ? <div className="p-8" data-id="element-549">
            <div className="mb-6" data-id="element-550">
              <h2 className="font-jakarta text-2xl font-extrabold text-[#0F0E0C] mb-1" data-id="element-551">
                Request a Demo
              </h2>
              <p className="text-sm text-[#9B9890] font-inter" data-id="element-552">
                Our team will get in touch with you shortly.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-553">
              {[{
            label: 'Full Name',
            key: 'name',
            type: 'text',
            placeholder: 'Rahul Sharma',
            required: true
          }, {
            label: 'Email',
            key: 'email',
            type: 'email',
            placeholder: 'rahul@company.com',
            required: true
          }, {
            label: 'Phone Number',
            key: 'phone',
            type: 'tel',
            placeholder: '+91 98765 43210',
            required: true
          }].map(field => <div key={field.key} data-id="element-554">
                  <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-555">
                    {field.label} {field.required && '*'}
                  </label>
                  <input type={field.type} required={field.required} value={(form as any)[field.key]} onChange={e => setForm({
              ...form,
              [field.key]: e.target.value
            })} placeholder={field.placeholder} className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-556" />
                </div>)}
              <div data-id="element-557">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-558">
                  Role *
                </label>
                <select required value={form.role} onChange={e => setForm({
              ...form,
              role: e.target.value
            })} className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all appearance-none" data-id="element-559">
                  <option value="" data-id="element-560">Select your role</option>
                  {roles.map(r => <option key={r} value={r} data-id="element-561">
                      {r}
                    </option>)}
                </select>
              </div>
              <div data-id="element-562">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-563">
                  Company
                </label>
                <input type="text" value={form.company} onChange={e => setForm({
              ...form,
              company: e.target.value
            })} placeholder="Your company name" className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-564" />
              </div>

              {error && <p className="text-xs text-red-500 font-inter bg-red-50 px-3 py-2 rounded-lg" data-id="element-565">
                  {error}
                </p>}

              <motion.button type="submit" disabled={loading} whileHover={!loading ? {
            scale: 1.02
          } : {}} whileTap={!loading ? {
            scale: 0.98
          } : {}} className="w-full py-3 bg-[#E8500A] hover:bg-[#D14508] disabled:opacity-60 disabled:cursor-not-allowed text-white font-inter font-semibold rounded-xl transition-colors mt-2 flex items-center justify-center gap-2" data-id="element-566">
                {loading ? <>
                    <Loader2 className="w-4 h-4 animate-spin" data-id="element-567" />
                    Sending...
                  </> : 'Request Demo'}
              </motion.button>
            </form>
          </div> : <div className="p-8 text-center" data-id="element-568">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-569">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-570" />
            </motion.div>
            <h3 className="font-jakarta text-xl font-extrabold text-[#0F0E0C] mb-2" data-id="element-571">
              Request Received!
            </h3>
            <p className="text-sm text-[#5C5A55] font-inter leading-relaxed mb-6" data-id="element-572">
              Our team will get in touch with you shortly.
            </p>
            <motion.button onClick={onClose} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="px-6 py-2.5 bg-[#0F0E0C] text-white font-inter font-semibold rounded-xl text-sm" data-id="element-573">
              Close
            </motion.button>
          </div>}
      </motion.div>
    </motion.div>;
}